import React, { useEffect, useMemo, useState } from 'react';
import { SDK } from 'vim-os-js-browser/types';
import { loadSdk } from 'vim-os-js-browser';
import {
  VimOSContext,
  VimOSPatientProvider,
  VimOSEncounterProvider,
} from 'src/hooks/vim/hooks/providers';
import { AppLoader } from './components/AppLoader';
import { AppConfigProvider } from 'src/hooks/vim/hooks/providers/AppConfigContext';
import { logger } from './services';

// Check if running in an iframe (embedded in Vim EHR)
const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

export const AppWrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [vimOS, setVimOS] = useState<SDK | undefined>(undefined);
  const [loading, setLoading] = useState(isEmbedded());

  useEffect(() => {
    // Only load SDK if embedded in iframe
    if (!isEmbedded()) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const vimOsSdk = await loadSdk();
        setVimOS(vimOsSdk);
      } catch (err) {
        logger.error('Failed to load Vim SDK:', {}, err as Error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sdk = useMemo(() => vimOS, [vimOS]);

  if (loading) {
    return <AppLoader />;
  }

  // If SDK loaded, provide context
  if (sdk) {
    return (
      <VimOSContext.Provider value={sdk}>
        <AppConfigProvider>
          <VimOSPatientProvider>
            <VimOSEncounterProvider>{children}</VimOSEncounterProvider>
          </VimOSPatientProvider>
        </AppConfigProvider>
      </VimOSContext.Provider>
    );
  }

  // Otherwise, just render children
  return <>{children}</>;
};
